import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "mobile-header-menu fixed left-0 top-0 z-50 flex w-[calc(80vw+18px)] flex-col bg-background text-xs shadow-lg",
  role: "dialog",
  "aria-modal": "true",
  "aria-label": "Navigation menu"
}
const _hoisted_2 = { class: "flex h-14 shrink-0 items-center justify-between border-b border-border bg-topbar px-4" }
const _hoisted_3 = ["src"]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)_+_75px)]" }
const _hoisted_6 = {
  key: 0,
  class: "flex flex-col py-2"
}
const _hoisted_7 = ["href", "onClick"]
const _hoisted_8 = ["href"]
const _hoisted_9 = { class: "border-t border-border bg-topbar" }
const _hoisted_10 = ["href"]
const _hoisted_11 = { class: "px-2 py-4" }

import { computed, ref, inject, onMounted, onBeforeUnmount } from 'vue';
import { subscribeKey, unsubscribeKey } from '@drapejs/core';
import { X, CircleHelp, Gift } from 'lucide-vue-next';

import useContext from '@/composables/useContext';
import useGiftCard from '@/composables/useGiftCard';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import useGiftProductListing, { generateHtmlElementId } from '@/composables/useGiftProductListing';

import Logout from '@/components/header/Logout.vue';
import LocaleSelector from '@/components/LocaleSelector.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'MobileHeaderMenu',
  props: {
    links: {},
    logoUrl: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {



const emit = __emit;

const { channel, header, navigate } = useContext();
const { activeGiftCard } = useGiftCard();
const { websiteText } = useWebsiteTexts();
const { productSections } = useGiftProductListing();

const hasActiveGiftCard = computed(() => (activeGiftCard.value?.giftCards?.length ?? 0) > 0);

const customerServiceLink = computed<any>(() => header.value?.topNavigatonLink);
const customerServiceUrl = computed(() => customerServiceLink.value?.url);
const customerServiceLabel = computed(() => customerServiceLink.value?.label);

const redeemUrl = computed(() => channel.value?.rootPath || '/');

const cacheSubscribe = inject<(key: string, handler: (val: any) => void, options?: { immediate?: boolean }) => void>(subscribeKey, () => {});
const cacheUnsubscribe = inject<(key: string, handler: (val: any) => void) => void>(unsubscribeKey, () => {});

const categoryUrl = ref('');
function onCategoryUrl(val: any) {
  categoryUrl.value = val || '';
}
onMounted(() => cacheSubscribe('__categoryUrl', onCategoryUrl, { immediate: true }));
onBeforeUnmount(() => cacheUnsubscribe('__categoryUrl', onCategoryUrl));

const listingUrl = computed(() => categoryUrl.value || activeGiftCard.value?.firstCategory?.url || '');

const categories = computed<{ id: string; label: string; href: string }[]>(() => {
  const sections = (productSections.value || []) as any[];
  return sections
    .filter((s: any) => s.tags?.length > 0 && s.products?.length > 0)
    .map((s: any) => {
      const tag = s.tags[0];
      const id = generateHtmlElementId(tag.value);
      const base = listingUrl.value;
      const href = base
        ? `${base}${base.includes('?') ? '&' : '?'}scrollTo=${encodeURIComponent(tag.value || '')}`
        : id;
      return {
        id,
        label: tag.displayName || tag.value || '',
        href
      };
    });
});

function stickyHeight(selector: string) {
  const node = document.querySelector(selector) as HTMLElement | null;
  return node && node.offsetHeight > 0 ? node.getBoundingClientRect().height : 0;
}

function onCategoryClick(cat: { id: string; href: string }) {
  emit('close');

  if (document.getElementById(cat.id)) {
    setTimeout(() => {
      const el = document.getElementById(cat.id);
      if (!el) return;
      const headerH = stickyHeight('.site-header__mobile') || stickyHeight('.site-header__desktop');
      const navH = stickyHeight('.category-sticky-nav--mobile') || stickyHeight('.category-sticky-nav--desktop');
      const top = el.getBoundingClientRect().top + window.scrollY - headerH - navH;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 100);
    return;
  }

  if (!cat.href || cat.href.startsWith('#')) return;

  const target = new URL(cat.href, window.location.origin);
  navigate(target.pathname + target.search);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("aside", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (_ctx.logoUrl)
        ? (_openBlock(), _createElementBlock("img", {
            key: 0,
            src: _ctx.logoUrl,
            alt: "logo",
            class: "h-6"
          }, null, 8, _hoisted_3))
        : (_openBlock(), _createElementBlock("span", _hoisted_4)),
      _createElementVNode("button", {
        type: "button",
        class: "btn-bare -mr-2 p-2 text-foreground",
        "aria-label": "Close menu",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close')))
      }, [
        _createVNode(_unref(X), { class: "h-5 w-5" })
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      (hasActiveGiftCard.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            (categories.value.length)
              ? (_openBlock(), _createElementBlock("nav", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (cat) => {
                    return (_openBlock(), _createElementBlock("a", {
                      key: cat.id,
                      href: cat.href,
                      class: "border-b border-border px-4 py-3 text-sm uppercase font-normal text-foreground no-underline last:border-b-0 hover:underline",
                      onClick: _withModifiers(($event: any) => (onCategoryClick(cat)), ["prevent"])
                    }, _toDisplayString(cat.label), 9, _hoisted_7))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ], 64))
        : (_openBlock(), _createElementBlock("a", {
            key: 1,
            href: redeemUrl.value,
            class: "animate-dark-gradient mx-3 my-4 flex items-center gap-3 rounded-xl visited:text-white px-5 py-4 text-sm font-medium text-white no-underline shadow-lg"
          }, [
            _createVNode(_unref(Gift), { class: "h-5 w-5 text-white/80" }),
            _createElementVNode("span", null, _toDisplayString(_unref(websiteText)('header__redeem_giftcard').value || 'Lös in gåvokort'), 1)
          ], 8, _hoisted_8)),
      _createElementVNode("div", _hoisted_9, [
        (customerServiceUrl.value)
          ? (_openBlock(), _createElementBlock("a", {
              key: 0,
              href: customerServiceUrl.value,
              class: "flex items-center gap-3 border-b border-border visited:text-foreground px-4 py-3 text-sm text-foreground no-underline hover:underline",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('close')))
            }, [
              _createVNode(_unref(CircleHelp), { class: "h-4 w-4 text-muted-foreground" }),
              _createElementVNode("span", null, _toDisplayString(customerServiceLabel.value), 1)
            ], 8, _hoisted_10))
          : _createCommentVNode("", true),
        (hasActiveGiftCard.value)
          ? (_openBlock(), _createBlock(Logout, {
              key: 1,
              "show-icon": true,
              class: "flex cursor-pointer items-center gap-3 border-b border-border px-4 py-3 text-sm text-foreground hover:underline"
            }))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_11, [
          _createVNode(LocaleSelector)
        ])
      ])
    ])
  ]))
}
}

})